// My Products & Tools — client-facing hub powered by the JC3 products backend.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, RotateCcw, Sparkles, CheckCircle2 } from "lucide-react";
import BrandShell from "../components/BrandShell";
import { useAuth } from "../auth/AuthContext";

const BASE = process.env.REACT_APP_PRODUCTS_API_URL;

// Compliance guard — never render these phrases, even if the API sends them.
const BANNED = [
  /credit repair/i, /guaranteed approval/i, /remove negative items/i, /bad credit/i,
  /fix your credit/i, /secured grants/i, /buy a tradeline/i, /we guarantee/i, /promise to delete/i,
];
const cleanText = (s) => (s && !BANNED.some((re) => re.test(s)) ? s : null);

// Official service names — clients never see internal/API product names or vendors.
const SERVICE_NAMES = {
  1: "Financial Profile Development",
  2: "Personal Capital Strategy",
  3: "Business Capital Strategy",
  4: "Account History Positioning",
  5: "Grant Readiness & Research",
  6: "Business Foundation Setup",
};
const SUMMARY_OVERRIDES = {
  1: "Financial profile analysis and development, built from your own 3-bureau report. Educational guidance — decided by you.",
  4: "Established-account positioning strategy matched to your profile.",
};
const displayStep = (s) =>
  s
    .replace(/Authorized User Credit Positioning/gi, "Account History Positioning")
    .replace(/dispute correspondence/gi, "accuracy-review correspondence")
    .replace(/dispute/gi, "accuracy review")
    .replace(/\(Product 6\)/g, "(Business Foundation Setup)")
    .replace(/Products 3 and 5/g, "Business Capital Strategy and Grant Readiness & Research");

/* ============================ Section 1: Products ============================ */

function ProductCard({ product, children }) {
  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="border border-jc-gold/25 bg-jc-navy/40 p-6 md:p-8 flex flex-col gap-5"
    >
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Service {product.id}</p>
        <h2 className="font-display text-xl md:text-2xl text-jc-white leading-tight">{SERVICE_NAMES[product.id] || cleanText(product.name)}</h2>
        <p className="mt-2 font-body text-sm text-jc-muted leading-relaxed">{SUMMARY_OVERRIDES[product.id] || cleanText(product.summary)}</p>
        {cleanText(product.complianceNote) ? (
          <p data-testid={`compliance-note-${product.id}`} className="mt-3 font-body text-[12px] italic text-jc-muted/80 leading-relaxed">
            {product.complianceNote}
          </p>
        ) : null}
      </div>

      {product.clientSteps?.length ? (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-jc-gold/80 mb-2.5">Your next steps</p>
          <ol className="space-y-1.5 list-none">
            {product.clientSteps.filter(cleanText).map((s, i) => (
              <li key={i} className="flex gap-2.5 font-body text-[13.5px] text-jc-white/80 leading-relaxed">
                <span className="jc-num text-jc-gold flex-shrink-0" style={{ fontSize: "13px" }}>{String(i + 1).padStart(2, "0")}</span>
                {displayStep(s)}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {children}
    </article>
  );
}

/* ================ Section 2: Business Foundation Checklist ================= */

function FormationChecklist({ userId }) {
  const storageKey = `jc3_formation_${userId || "anon"}`;
  const [stages, setStages] = useState(null);
  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); } catch { return new Set(); }
  });
  const [status, setStatus] = useState(null);

  const syncStatus = useCallback(async (ids) => {
    try {
      const res = await fetch(`${BASE}/api/formation/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completedStepIds: ids }),
      });
      if (res.ok) setStatus((await res.json()).status);
    } catch (e) { console.warn("formation status sync failed — keeping last known status", e); }
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${BASE}/api/formation/checklist`)
      .then((r) => r.json())
      .then((d) => { if (alive) setStages(d.stages || []); })
      .catch(() => { if (alive) setError(true); });
    syncStatus([...checked]);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
    localStorage.setItem(storageKey, JSON.stringify([...next]));
    syncStatus([...next]);
  };

  const stageComplete = useMemo(() => {
    if (!stages) return [];
    return stages.map((s) => s.steps.every((st) => checked.has(st.id)));
  }, [stages, checked]);

  if (error) return <p className="text-sm text-jc-muted font-body">Checklist unavailable right now.</p>;
  if (!stages) return <div className="h-32 bg-jc-navy/40 border border-jc-gold/15 animate-pulse" />;

  const pct = status?.percentComplete ?? 0;
  const unlocked = status?.unlockedProducts || [];
  const fundingUnlocked = unlocked.includes(3) || unlocked.includes(5);

  return (
    <div data-testid="formation-checklist" className="border border-jc-gold/25 bg-jc-navy/40 p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-2">◆ Business Foundation Checklist</p>
          <h2 className="font-display text-xl md:text-2xl text-jc-white leading-tight">
            Stage {status?.currentStage ?? 1} — {status?.currentStageName ?? stages[0]?.name}
          </h2>
          {status?.nextStep && !status?.complete ? (
            <p data-testid="formation-next-step" className="mt-2 font-body text-sm text-jc-muted">
              Next step: <span className="text-jc-gold">{status.nextStep.label}</span>
            </p>
          ) : null}
        </div>
        {fundingUnlocked && (
          <span data-testid="funding-unlocked-badge" className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-jc-gold bg-jc-gold/15 text-jc-gold font-mono text-[10px] uppercase tracking-[0.22em]">
            <Sparkles size={12} /> Funding services unlocked
          </span>
        )}
      </div>

      <div className="mt-5">
        <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-jc-muted mb-1.5">
          <span>Progress</span><span data-testid="formation-pct" className="text-jc-gold">{pct}%</span>
        </div>
        <div className="h-2 bg-jc-black/60 border border-jc-gold/20 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-jc-gold to-jc-gold-light transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mt-7 grid gap-5 lg:grid-cols-2">
        {stages.map((s, idx) => {
          const locked = idx > 0 && !stageComplete[idx - 1];
          return (
            <div
              key={s.stage}
              data-testid={`formation-stage-${s.stage}`}
              className={`border p-5 transition-opacity ${locked ? "border-jc-gold/10 opacity-45" : "border-jc-gold/25"} bg-jc-black/40`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-jc-gold">Stage {s.stage} · {s.name}</p>
                {locked ? <Lock size={13} className="text-jc-muted" /> : stageComplete[idx] ? <CheckCircle2 size={14} className="text-jc-gold" /> : null}
              </div>
              <p className="font-body text-[12.5px] text-jc-muted mb-3.5">{s.goal}</p>
              <ul className="space-y-2.5">
                {s.steps.map((st) => (
                  <li key={st.id}>
                    <label className={`flex items-start gap-3 select-none ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={checked.has(st.id)}
                        disabled={locked}
                        onChange={() => toggle(st.id)}
                        data-testid={`formation-step-${st.id}`}
                        className="mt-0.5 h-4 w-4 accent-[#C8A46A] flex-shrink-0"
                      />
                      <span className={`font-body text-[13.5px] leading-relaxed ${checked.has(st.id) ? "text-jc-muted line-through" : "text-jc-white/85"}`}>
                        {st.label}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================== Page =================================== */

export default function ClientProducts() {
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", products: [] });

  const load = useCallback(async () => {
    setState({ status: "loading", products: [] });
    try {
      const res = await fetch(`${BASE}/api/products`);
      if (!res.ok) throw new Error();
      const d = await res.json();
      const products = [...(d.products || [])].sort((a, b) => a.id - b.id);
      setState({ status: "done", products });
    } catch {
      setState({ status: "error", products: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <BrandShell dashboardChrome>
      <div className="bg-jc-black min-h-[calc(100vh-5rem)]">
        <div className="px-6 md:px-12 py-12 max-w-6xl mx-auto" data-testid="client-products-page">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ Your Services</p>
          <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 0.95 }}>
            Your services. Your progress. Your next steps.
          </h1>
          <p className="mt-4 text-jc-muted max-w-2xl font-body">
            Every service you're enrolled in, where you stand, and exactly what to do next — educational guidance, decided by you.
          </p>

          {state.status === "loading" && (
            <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="products-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-56 border border-jc-gold/15 bg-jc-navy/40 animate-pulse" />
              ))}
            </div>
          )}

          {state.status === "error" && (
            <div className="mt-10 border border-dashed border-jc-gold/30 p-10 text-center" data-testid="products-error">
              <p className="font-body text-jc-muted">Couldn't load your tools — retry</p>
              <button
                onClick={load}
                data-testid="products-retry-btn"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border border-jc-gold text-jc-gold font-body font-semibold text-[12px] uppercase tracking-[0.08em] hover:bg-jc-gold hover:text-jc-black focus:outline-none focus:ring-2 focus:ring-jc-gold/50 transition-colors"
                style={{ borderRadius: "2px" }}
              >
                <RotateCcw size={13} /> Retry
              </button>
            </div>
          )}

          {state.status === "done" && (
            <>
              <div className="mt-10 grid gap-5 md:grid-cols-2">
                {state.products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              <div className="mt-8">
                <FormationChecklist userId={user?.id} />
              </div>
            </>
          )}
        </div>
      </div>
    </BrandShell>
  );
}
